import React, { useState, useMemo, forwardRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronDown } from "lucide-react";
import { WindDensityResponse } from "../../../services/api";
import { WIND_HEIGHTS } from "../../../utils/windChartConstants";

interface PowerDensityChartProps {
  data: WindDensityResponse | null;
  selectedHeight: string;
  onHeightChange?: (height: string) => void;
}

export const PowerDensityChart = forwardRef<HighchartsReact.RefObject, PowerDensityChartProps>(
  ({ data, selectedHeight, onHeightChange }, ref) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const monthly = data?.monthly_data ?? new Array(12).fill(0);
  const annualMean = data?.annual_mean ?? 0;
  const totalAccumulated = data?.total_accumulated ?? 0;

  const root = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const primaryColor = root?.getPropertyValue('--chart-2').trim() || "#4A90D9";
  const secondaryColor = root?.getPropertyValue('--chart-5').trim() || "#0c2856";
  const textSecondary = root?.getPropertyValue('--color-gray-light').trim() || "#61656d";
  const gridLineColor = root?.getPropertyValue('--color-gray-pale').trim() || "#d9d9d9";
  const axisLineColor = root?.getPropertyValue('--border').trim() || "#d9d9d9";
  const textPrimary = root?.getPropertyValue('--foreground').trim() || "#000000";
  const backgroundColor = root?.getPropertyValue('--background').trim() || "#ffffff";
  const borderColor = root?.getPropertyValue('--border').trim() || "#d9d9d9";

  const chartColor = "#4ADE80";

  const seriesData = useMemo(() => [
    ...monthly.map((density) => ({ y: density, color: chartColor })),
    { y: parseFloat(annualMean.toFixed(2)), color: secondaryColor, name: "Média" },
  ], [monthly, annualMean, secondaryColor]);

  const options: Highcharts.Options = useMemo(() => ({
    chart: { type: "column", backgroundColor: "transparent", height: 320, marginTop: 40, marginLeft: 60, marginRight: 20, spacingBottom: 5 },
    title: { text: undefined },
    legend: { enabled: false },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez","Média"],
      crosshair: true,
      labels: { step: 2, style: { fontSize: "11px", color: textSecondary } },
      lineColor: axisLineColor,
      tickColor: 'transparent',
    },
    yAxis: {
      min: 0,
      title: { text: "W/m²", margin: 15, style: { fontSize: "11px", color: textSecondary, fontWeight: "normal" } },
      labels: { style: { fontSize: "11px", color: textSecondary } },
      gridLineColor: gridLineColor,
      plotLines: annualMean > 0 ? [{
        color: secondaryColor,
        width: 1.5,
        value: annualMean,
        dashStyle: "Dash" as const,
        zIndex: 5,
      }] : [],
    },
    tooltip: {
      backgroundColor, borderColor, borderRadius: 10, padding: 10, shadow: true, useHTML: true,
      headerFormat: `<div style="font-size: 11px; color: ${textSecondary}; margin-bottom: 6px;">{point.key}</div>`,
      pointFormat: `<div style="font-weight: bold; color: ${textPrimary}; font-size: 12px;"><span style="color: {point.color};">●</span> {point.y:.2f} W/m²</div>`,
    },
    plotOptions: {
      column: {
        pointWidth: 16, borderWidth: 0, borderRadius: 10,
        states: { hover: { borderWidth: 2, borderColor: secondaryColor } },
      },
    },
    series: [{ type: "column", name: "Densidade de Potência", data: seriesData }],
  }), [seriesData, annualMean, textSecondary, axisLineColor, gridLineColor, secondaryColor, backgroundColor, borderColor, textPrimary]);

  return (
    <div className="flex flex-col relative pb-4 bg-white rounded-lg shadow-md">
      <div className="absolute top-0 right-0 z-10">
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(prev => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium text-[#64748B] hover:text-[#0C2856] transition-colors"
          >
            Altura {selectedHeight}
            <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-[110px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
              {WIND_HEIGHTS.map((h) => (
                <button key={h}
                  onClick={() => { onHeightChange?.(h); setDropdownOpen(false); }}
                  className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                    ${selectedHeight === h ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                >
                  {h}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
      </div>

      <div className="flex justify-end items-center gap-8 mt-3 pr-4">
        <StatItem label="Média Anual" value={`${annualMean.toFixed(2)} W/m²`} />
        <StatItem label="Total Acumulado" value={`${totalAccumulated.toFixed(2)} W/m²`} />
      </div>
    </div>
  );
});

PowerDensityChart.displayName = "PowerDensityChart";

const StatItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-center">
    <span className="text-[12px] text-[#0C2856] font-medium mb-1">{label}</span>
    <span className="text-[14px] font-bold text-[#1E293B]">{value}</span>
  </div>
);
